import React, { useRef, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext"; // Assuming you have an AuthContext

const withRoleProtection = (WrappedComponent, allowedRoles) => {
  return (props) => {
    const { currentUser, userRole, loading } = useAuth(); // Hooks to get user and role
    // Cache the role once it's loaded to prevent losing it during re-renders
    const cachedRoleRef = useRef(null);
    
    // Update cached role when userRole is loaded
    useEffect(() => {
      if (userRole) {
        cachedRoleRef.current = userRole;
      }
    }, [userRole]);
    
    // Use cached role if current role is null but we had one before
    const effectiveRole = userRole || cachedRoleRef.current;

    console.log("🔒 withRoleProtection check:", {
      user: currentUser?.email,
      userRole,
      cachedRole: cachedRoleRef.current,
      effectiveRole,
      allowedRoles,
      loading,
      isAllowed: allowedRoles.includes(effectiveRole),
    });

    // Show loading while auth state is being determined
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundColor: "background.default",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }

    // Not logged in - redirect to login
    if (!currentUser) {
      console.log("❌ No user logged in, redirecting to /login");
      return <Navigate to="/login" replace />;
    }

    // User is logged in but role hasn't loaded yet - wait (unless we have a cached role)
    if (currentUser && !effectiveRole && !cachedRoleRef.current) {
      console.log("⏳ User logged in but role not loaded yet, waiting...");
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundColor: "background.default",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your profile...
          </Typography>
        </Box>
      );
    }

    // If effectiveRole exists but is not in allowed roles, deny access
    if (effectiveRole && !allowedRoles.includes(effectiveRole)) {
      console.log(
        "🚫 Access denied! User role:",
        effectiveRole,
        "Allowed roles:",
        allowedRoles
      );
      return <Navigate to="/unauthorized" replace />;
    }
    
    // If we have a cached role, use it even if current role is temporarily null
    if (cachedRoleRef.current && allowedRoles.includes(cachedRoleRef.current)) {
      console.log("✅ Using cached role:", cachedRoleRef.current);
      return <WrappedComponent {...props} />;
    }

    // User has correct role - render component
    if (effectiveRole && allowedRoles.includes(effectiveRole)) {
      console.log("✅ Access granted! Rendering component for role:", effectiveRole);
      return <WrappedComponent {...props} />;
    }

    // Fallback: if we have a user but no role after loading, show loading (don't redirect)
    if (currentUser && !effectiveRole && !loading) {
      console.warn("⚠️ User is logged in but role is null after loading. Waiting...");
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundColor: "background.default",
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your profile...
          </Typography>
        </Box>
      );
    }

    // Should not reach here, but just in case
    return <WrappedComponent {...props} />;
  };
};

export default withRoleProtection;
